/**
 * The Spatial kitchen palette.
 *
 * <p>These literals are only FALLBACKS. Every block reads the tenant's token
 * CSS variables first (`--color-brand-500`, ...), so an operator re-colouring
 * the theme in the admin changes the storefront without a deploy. The template
 * seeds the same values into those tokens.
 *
 * <p>The look is the site mockup's (`spatial/mockups/site/assets/site.css`,
 * owner-approved 2026-09-28): warm paper surfaces, a deep green brand and brass
 * accents. The key names predate it and are kept so every block's `--sk-*`
 * variable still resolves: `sage` is the brand green, `warm` the paper page
 * background, `stone` the second paper tone, `sand` the brass.
 *
 * <h3>Contrast decisions (WCAG 2.2 AA)</h3>
 * Measured with {@link contrastRatio}; asserted in `test/contrast.test.ts`.
 * <ul>
 *   <li>The green brand colours pass for normal text on every light surface,
 *       and white text passes on both.</li>
 *   <li>Brass (`sand`) fails as text and under white text (3.5:1). It is a
 *       background for `ink` text or pure decoration only. Brass-coloured TEXT,
 *       such as eyebrows, uses the darker `brassText`.</li>
 *   <li>`faint` is the lightest text colour allowed; the mockup's lighter
 *       `#7b817c` failed on paper and is not used.</li>
 *   <li>`clay` carries white text (count badges) at 5.25:1.</li>
 *   <li>The light border is decorative. Form controls use `controlBorder` to
 *       meet the 3:1 non-text contrast rule.</li>
 *   <li>Focus rings are 2 px `sageDeep`, offset from the element.</li>
 * </ul>
 */
export const palette = {
  ink: '#1D211F',
  muted: '#4A504C',
  faint: '#646A65',
  sage: '#2E4A3E',
  sageDeep: '#233A30',
  stone: '#EFE8DE',
  stoneDeep: '#E2D9CC',
  sand: '#A8834A',
  brassText: '#7A5E2F',
  brassSoft: '#F3E8D6',
  clay: '#A4573A',
  white: '#FFFDF9',
  warm: '#F7F3ED',
  sageGrey: '#E3EBE5',
  border: '#E2D9CC',
  controlBorder: '#857A6B',
} as const;

export type PaletteKey = keyof typeof palette;

function channel(value: number): number {
  const v = value / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance of a `#RRGGBB` colour. */
export function relativeLuminance(hex: string): number {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two `#RRGGBB` colours (1 to 21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
